import { isClosedStatus } from '@bbbk/schema';
import type { ReactNode } from 'react';
import type { BookingCapacity, Viewing } from '@bbbk/schema';
import { acceptedLabel, factLabel } from '../bookings/requestFacts';
import { AreaPill, DogCell, StatusPill } from '../common';
import { VIEWING_STATUS_RECORDS } from '../bookings/constants';
import { compareViewings, viewingDogBreeds, viewingFlags, viewingSlotLabel } from './viewings';
import { deriveBookingFlags } from '../bookings/flags';
import {
  amountDue,
  derivePaymentStatus,
  dogsOf,
  displayRatePerNight,
  formatOwnerLabel,
  nightlyRateLabel,
  nightsBetween,
  nightsLabel,
} from '../bookings/domain';
import { AREA_LABELS } from '../bookings/constants';
import { dateOnly, fmtDate, fmtDateRange } from '../../lib/dates';
import { fmtEuro } from '../../lib/money';
import { dayName } from '../staff/week';
import type { Booking } from '../bookings/types';

// THE shared stay list — one component, two panels (daily-command-strip AC15).
//
// Needs attention and In residence are the same booking rows filtered
// differently, so they render through this and are incapable of describing a
// stay two different ways. They sit inches apart on the same page; two tables
// would be two chances to disagree.
//
// The column set is the BOOKINGS TABLE's, not the prototype's (Freddi,
// 2026-08-17 — "follow app conventions"). Three consequences worth naming,
// because the prototype drew all three differently:
//
//  - The owner rides INSIDE the dog cell, as it does everywhere else in the
//    app, so there is no separate owner column. A second one would print the
//    owner twice on every row.
//  - Area is `AreaPill`, which renders plain text — the app dropped the badge
//    app-wide ("Area reads as plain cell data now").
//  - The dog cell's markers are `deriveBookingFlags`, the app's single
//    derivation, so the vaccination indicator and the care flags are exactly
//    the ones the desk and the bookings table already show. The prototype's
//    worded chip and coloured dots are not built.
//
// Read-only throughout: the status pill takes no onClick, and a row click opens
// the booking. Changing anything is the desk's job or the modal's.
//
// A ROW IS REACHABLE BY KEYBOARD. It stays a <tr> — the table semantics are the
// point — but carries a tab stop and Enter/Space, because the only way into a
// listed booking was otherwise a mouse (PR #164, codex P2). The app's shared
// DataTable has the same gap; this does not wait for it.

export interface StayTableProps {
  rows: Booking[];
  onOpenBooking: (bookingId: string) => void;
  // VIEWINGS IN THE SAME TABLE (viewings-in-needs-attention AC5). A second array
  // rather than a union over `rows`, so the callers that pass bookings are
  // untouched. They are placed among the rows by date — see `interleave` — and
  // never re-order them.
  //
  // A viewing has no area, no nights and no balance. Those cells carry an em
  // dash rather than a blank: an empty cell reads as a value that failed to
  // load, a dash as one that was never owed.
  viewings?: Viewing[];
  onOpenViewing?: (v: Viewing) => void;
  // Prefix the check-in with its weekday ("Tue 18 Aug → 24 Aug"). The chase
  // list is read to decide who to telephone first, and "Tue" is more actionable
  // there than the date alone. In-residence omits it — that list is ordered by
  // departure and the arrival weekday is noise.
  showArrivalDay?: boolean;
  /** Status as the FIRST column — the Needs attention panel's order (R-DESK-12, `sdq`). */
  statusFirst?: boolean;
  // Labels the scroll region for assistive tech; each panel names its own.
  label: string;
  /* THE FACT ON A REQUEST'S ROW (accept-or-decline AC3, decision 270):
     *Big · 12 of 14 those nights* under its dates, amber when tight. No
     buttons — Freddi, 2026-10-10: the decision is made on the booking, in
     its strip, not from a list (deviation 2). */
  decide?: StayDecide;
}

export interface StayDecide {
  /** The fact per request, by booking id; absent until read. */
  capacity: Record<string, BookingCapacity | undefined>;
}

// The app's three payment states, said in words a person would use. "Part paid"
// rather than "partial": the row is read aloud down a telephone.
const PAY_WORD: Record<'unpaid' | 'partial' | 'paid', string> = {
  unpaid: 'Unpaid',
  partial: 'Part paid',
  paid: 'Paid',
};

export function StayTable({ rows, onOpenBooking, viewings = [], onOpenViewing, showArrivalDay = false, statusFirst = false, label, decide }: StayTableProps) {
  return (
    <div className="stay-scroll" role="region" aria-label={label} tabIndex={0}>
      <table className="stay-table">
        <thead>
          <tr>
            {statusFirst && <th scope="col">Status</th>}
            <th scope="col">Dog(s)</th>
            <th scope="col" className="st-opt">Breed</th>
            <th scope="col" className="st-opt">Area</th>
            <th scope="col">Stay</th>
            <th scope="col" className="st-num">Due</th>
            {!statusFirst && <th scope="col">Status</th>}
          </tr>
        </thead>
        <tbody>{interleave(rows, viewings, onOpenBooking, onOpenViewing, showArrivalDay, statusFirst, decide)}</tbody>
      </table>
    </div>
  );
}

// THE CALLER'S ORDER IS NEVER TOUCHED. Each viewing is placed before the first
// booking arriving on or after its day — so a viewing lands AMONG the arrivals,
// and on a shared day ahead of them (Sir, 2026-09-02) — and the viewings among
// themselves take the domain's one order, morning first.
//
// It used to be a sort over both kinds together, run only when a viewing was
// present: `ResidencePanel` (sorted by check-OUT, no viewings) was re-sorted by
// arrival until a guard was added, and behind the guard the sort still
// re-ordered the BOOKINGS by check-in and then by id as a string, so logging
// the first viewing of the week quietly reshuffled same-day arrivals, and
// "b10" filed before "b9". A merge that never moves a booking cannot do either
// (review, PR #173). A caller that passes viewings passes rows in check-in
// order, which is what makes the placement read as date order.
function interleave(
  rows: Booking[],
  viewings: Viewing[],
  onOpenBooking: (id: string) => void,
  onOpenViewing: ((v: Viewing) => void) | undefined,
  showArrivalDay: boolean,
  statusFirst = false,
  decide?: StayDecide,
): ReactNode[] {
  const queue = [...viewings].sort(compareViewings);
  const out: ReactNode[] = [];
  let next = 0;
  const viewing = (v: Viewing) => <ViewingStayRow key={'v' + v.id} v={v} onOpen={onOpenViewing} statusFirst={statusFirst} />;
  for (const b of rows) {
    const day = dateOnly(b.checkIn);
    while (next < queue.length && dateOnly(queue[next]!.viewingOn) <= day) out.push(viewing(queue[next++]!));
    out.push(<BookingStayRow key={b.id} b={b} onOpen={onOpenBooking} showArrivalDay={showArrivalDay} statusFirst={statusFirst} decide={decide} />);
  }
  while (next < queue.length) out.push(viewing(queue[next++]!));
  return out;
}

// A BOOKING IN THE CHASE LIST or the block — the row every caller passed before
// viewings existed, unchanged in what it renders.
function BookingStayRow({ b, onOpen, showArrivalDay, statusFirst = false, decide }: { b: Booking; onOpen: (id: string) => void; showArrivalDay: boolean; statusFirst?: boolean; decide?: StayDecide }) {
  const cap = decide && b.status === 'requested' ? decide.capacity[b.id] : undefined;
  const breed = [...new Set(b.dogs.map((d) => d.breed).filter(Boolean))].join(' · ');
  const rate = displayRatePerNight(b);
  const pay = derivePaymentStatus(b);
  // fmtDateRange is the app's stay-range idiom (bookings table, desk
  // row); the weekday is prefixed rather than a second format invented.
  const stay = showArrivalDay
    // The calendar API has been seen to return a full timestamp here, and
    // dayName appends T00:00:00 — which on a timestamp yields an invalid
    // date and printed "undefined" (PR #164, codex P2). dateOnly is the
    // single normaliser; needsAttention.ts reads the same one.
    ? `${dayName(dateOnly(b.checkIn))} ${fmtDateRange(b.checkIn, b.checkOut)}`
    : fmtDateRange(b.checkIn, b.checkOut);
  const dogs = dogsOf(b);
  const names = b.dogs.map((d) => d.name).join(', ');
  return (
    <tr
      onClick={() => onOpen(b.id)}
      onKeyDown={(e) => {
        // Space scrolls the region it sits in unless stopped, and the
        // region is scrollable by design.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(b.id);
        }
      }}
      tabIndex={0}
      aria-label={`Open the booking for ${names}`}
      className="stay-row"
    >
      {statusFirst && (
        <td>
          <StatusPill status={b.status} />
        </td>
      )}
      <td>
        <DogCell
          dogs={b.dogs}
          ownerName={formatOwnerLabel(b.ownerContacts)}
          flags={isClosedStatus(b.status) ? [] : deriveBookingFlags(b)}
          meta={breed}
        />
        {/* HOUSEHOLD SIZE, stated rather than counted off the pile.
            The avatars overlap and the names truncate in a table this
            narrow, so a three-dog stay could read as one (AC15, PR
            #164 codex P2). Singles say nothing — a "×1" on every
            other row is noise. */}
        {dogs > 1 && (
          <span className="st-dogs-n" title={`${dogs} dogs on this booking`}>
            ×{dogs}
          </span>
        )}
      </td>
      <td className="st-opt st-breed">{breed}</td>
      <td className="st-opt">
        <AreaPill area={b.area} label={AREA_LABELS[b.area] ?? b.area} />
      </td>
      <td className="st-stay">
        <div className="st-stay-dates mono">{stay}</div>
        <div className="st-stay-nights">{nightsLabel(nightsBetween(b.checkIn, b.checkOut))}</div>
        {cap && <div className={`st-cap is-${cap.lead.level}`}>{factLabel(cap)}</div>}
        {cap && cap.othersAccepted > 0 && <div className="st-cap-others">{acceptedLabel(cap)}</div>}
        {/* A SCREENSHOT WAITING ON HER (deposit-proof AC8): said beneath the
            dates the way a request row carries its fact, so *asked and
            waiting* and *sent, check me* read apart without opening either. */}
        {b.proofPending && <div className="st-proof-in">Screenshot in</div>}
      </td>
      <td className="st-num">
        <div className="st-due amount">{fmtEuro(amountDue(b))}</div>
        {/* THE VERDICT, not just the arithmetic. A balance of €120
            says nothing about whether anything has been paid against
            it; an operator was left inferring "part paid" from the
            rate and the night count (PR #164, codex P2). The status
            column beside this is the BOOKING's, which is a different
            question. derivePaymentStatus is the app's own rule. */}
        <div className={'st-due-rate st-pay ' + pay}>
          {PAY_WORD[pay]}
          {rate != null && ` · ${nightlyRateLabel(rate)}`}
        </div>
      </td>
      {!statusFirst && (
        <td>
          <StatusPill status={b.status} />
        </td>
      )}
    </tr>
  );
}

// A VIEWING IN THE CHASE LIST. The same row shape, the same table, the same
// keyboard contract — and three cells that a viewing simply does not have,
// each carrying an em dash rather than a blank.
//
// THE SAME CELL AS THE BOOKING ROW BESIDE IT, and as the desk row: the dogs
// lead, the person sits beneath them, the breed line follows the booking's
// rule — one breed, or Mixed (Sir, 2026-08-27). It used to lead with the
// contact, print the dogs' NAMES under "Breed", and say "No phone given" where
// the desk had been told to leave a missing number blank (review, PR #173).
// The phone is not on this row at all, as an owner's is not on a booking's;
// the row opens the viewing, and the number is there.
const NONE = '\u2014';

function ViewingStayRow({ v, onOpen, statusFirst = false }: { v: Viewing; onOpen?: (v: Viewing) => void; statusFirst?: boolean }) {
  const breeds = viewingDogBreeds(v.dogs);
  const open = () => onOpen?.(v);
  return (
    <tr
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); // Space scrolls the region this sits in
          open();
        }
      }}
      tabIndex={0}
      aria-label={`Open the viewing for ${v.contactName}`}
      className="stay-row"
    >
      {statusFirst && (
        <td>
          <StatusPill record={VIEWING_STATUS_RECORDS[v.status]} />
        </td>
      )}
      <td>
        <DogCell
          dogs={v.dogs.map((d, i) => ({ id: `${v.id}-d${i}`, name: d.name ?? NONE, breed: d.breed ?? null }))}
          ownerName={v.contactName}
          flags={viewingFlags()}
          meta={breeds || undefined}
        />
      </td>
      <td className="st-opt st-breed">{breeds || NONE}</td>
      <td className="st-opt">{NONE}</td>
      <td className="st-stay">
        <div className="st-stay-dates mono">{`${dayName(dateOnly(v.viewingOn))} ${fmtDate(v.viewingOn)}`}</div>
        <div className="st-stay-nights">{viewingSlotLabel(v.slot)}</div>
      </td>
      <td className="st-num">
        <div className="st-due amount">{NONE}</div>
      </td>
      {!statusFirst && (
        <td>
          <StatusPill record={VIEWING_STATUS_RECORDS[v.status]} />
        </td>
      )}
    </tr>
  );
}
